const DEFAULT_ROOT = "Folder";
const ALLOWED_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "webp",
]);

export type DirectWriteEntry = {
  id: string;
  name: string;
  relativePath: string;
  fullPath: string;
  folderPath: string;
  handle: FileSystemFileHandle;
  parentHandle?: FileSystemDirectoryHandle;
  extension: string;
  isHidden: boolean;
};

export type DirectWriteIgnoredEntry = {
  name: string;
  relativePath: string;
  reason: "unsupported-extension";
};

export type DirectWriteStreamOptions = {
  signal?: AbortSignal;
  onWarning?: (warning: string) => void;
  onIgnored?: (entry: DirectWriteIgnoredEntry) => void;
  allowedExtensions?: Set<string>;
};

const toExtension = (name: string) => {
  const idx = name.lastIndexOf(".");
  if (idx === -1) return "";
  return name.slice(idx + 1).toLowerCase();
};

const buildEntry = (
  rootName: string,
  currentPath: string,
  name: string,
  handle: FileSystemFileHandle,
  parentHandle?: FileSystemDirectoryHandle
): DirectWriteEntry => {
  const relativePath = currentPath ? `${currentPath}/${name}` : name;
  const fullPath = rootName ? `${rootName}/${relativePath}` : relativePath;
  const folderPath = currentPath ? `${rootName}/${currentPath}` : rootName || DEFAULT_ROOT;
  const isHidden = name.startsWith(".") || name.startsWith("._");
  return {
    id: fullPath,
    name,
    relativePath,
    fullPath,
    folderPath,
    handle,
    parentHandle,
    extension: toExtension(name),
    isHidden,
  };
};

async function* walkDirectory(
  dir: FileSystemDirectoryHandle,
  rootName: string,
  currentPath: string,
  options: DirectWriteStreamOptions
): AsyncGenerator<DirectWriteEntry> {
  if (options.signal?.aborted) return;
  if (typeof dir.entries !== "function") {
    options.onWarning?.("Directory entries() not available; cannot traverse selected folder.");
    return;
  }

  try {
    for await (const [name, handle] of dir.entries()) {
      if (options.signal?.aborted) return;
      if (!handle || typeof handle !== "object") continue;
      if ((handle as any).kind === "directory") {
        const nextDir = handle as FileSystemDirectoryHandle;
        const nextPath = currentPath ? `${currentPath}/${name}` : name;
        yield* walkDirectory(nextDir, rootName, nextPath, options);
      } else if ((handle as any).kind === "file") {
        const extension = toExtension(name);
        const allowed = (options.allowedExtensions || ALLOWED_EXTENSIONS).has(extension);
        if (!allowed) {
          options.onIgnored?.({
            name,
            relativePath: currentPath ? `${currentPath}/${name}` : name,
            reason: "unsupported-extension",
          });
          continue;
        }
        const fileHandle = handle as FileSystemFileHandle;
        yield buildEntry(rootName, currentPath, name, fileHandle, dir);
      }
    }
  } catch (err) {
    options.onWarning?.(
      `Unable to traverse folder "${dir.name || rootName}": ${String((err as any)?.message || err)}`
    );
  }
}

export async function* streamDirectWriteEntries(
  handle: FileSystemDirectoryHandle,
  options: DirectWriteStreamOptions = {}
): AsyncGenerator<DirectWriteEntry> {
  if (!handle || (handle as any).kind !== "directory") {
    options.onWarning?.("Please select a real folder (not a virtual collection like Recents).");
    return;
  }

  const rootName = handle.name || DEFAULT_ROOT;
  yield* walkDirectory(handle, rootName, "", options);
}
